// Сочетания клавиш вне текста документа. Внутри редактора сочетания команд обрабатывает TipTap (keymap из
// registry.commands[].shortcut); сюда попадает то, что редактор не обработал: фокус на ленте, панели, в окне,
// а также раскладка «Русская» с Ctrl+Alt на Windows (ProseMirror сравнивает символ, здесь - физическая клавиша).
import { registry } from '../../core/registry';
import { runCommand } from '../../core/ctx';
import { appStore } from '../../core/store';
import { uiStore } from '../../ui/uiState';
import { closeFind } from './find';
import { MINI_OK } from '../../core/editor/active';

/** Команды, которые работают из любого места (даже из полей ввода окон и малых редакторов). */
const GLOBAL = new Set(['file.save', 'app.palette', 'app.help', 'sto.check', 'edit.find', 'edit.replace', 'view.focus',
  'file.print', 'view.ribbon', 'file.snapshot', 'app.shortcuts', 'view.zoomIn', 'view.zoomOut', 'view.zoom100', 'view.marks']);
/** Команды без открытого проекта (домашний экран). */
const NO_PROJECT = new Set(['app.palette', 'app.help', 'app.shortcuts']);

const CODE: Record<string, string[]> = {
  '=': ['Equal', 'NumpadAdd'], '-': ['Minus', 'NumpadSubtract'], '.': ['Period'], ',': ['Comma'], '/': ['Slash'],
  '0': ['Digit0', 'Numpad0'], Enter: ['Enter', 'NumpadEnter'], Space: ['Space'], ' ': ['Space'], '[': ['BracketLeft'],
  ']': ['BracketRight'], '\\': ['Backslash'], "'": ['Quote'], ';': ['Semicolon'], '`': ['Backquote'],
};

/** Совпадает ли событие с сочетанием в формате TipTap ('Mod-Shift-k', 'F7', 'Alt-Shift-ArrowUp'). */
export function matchShortcut(e: KeyboardEvent, sc: string): boolean {
  const parts = sc.split(/-(?!$)/);
  const key = parts.pop()!;
  const mods = new Set(parts.map((p) => (p === 'Mod' || p === 'Meta' || p === 'Cmd' ? 'Ctrl' : p)));
  if ((e.ctrlKey || e.metaKey) !== mods.has('Ctrl')) return false;
  if (e.altKey !== mods.has('Alt')) return false;
  if (e.shiftKey !== mods.has('Shift')) return false;
  if (/^[a-z]$/i.test(key)) return e.code === 'Key' + key.toUpperCase();
  if (/^[1-9]$/.test(key)) return e.code === 'Digit' + key || e.code === 'Numpad' + key;
  if (CODE[key]) return CODE[key].includes(e.code);
  return e.key === key;
}

function isTextInput(el: Element | null): boolean {
  if (!el) return false;
  const h = el as HTMLElement;
  if (h.closest?.('.sto-doc') && !h.closest?.('.mini-editor')) return false;   // основной редактор - не «поле ввода»
  return h.tagName === 'INPUT' || h.tagName === 'TEXTAREA' || h.tagName === 'SELECT' || !!h.isContentEditable
    || !!h.closest?.('math-field, .cm-editor, [contenteditable="true"]');
}

export function installGlobalKeys() {
  window.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.isComposing) return;
    // Esc вне текста (лента, панели): закрыть поиск, выйти из «Фокуса»
    if (e.key === 'Escape' && !document.querySelector('.modal, .popover, .palette, .slash-menu, .backstage')) {
      const u = uiStore.getState();
      if (u.find.open && !(e.target as HTMLElement)?.closest?.('.find-bar')) { e.preventDefault(); closeFind(); return; }
      if (u.focusMode) { e.preventDefault(); u.set({ focusMode: false }); return; }
    }
    if (!e.ctrlKey && !e.metaKey && !e.altKey && !/^F\d+$/.test(e.key)) return;   // только сочетания и F-клавиши
    const hasProject = !!appStore.getState().projectId;
    const inField = isTextInput(e.target as Element);
    const inModal = !!(e.target as HTMLElement)?.closest?.('.modal');
    for (const c of registry.commands.list()) {
      if (!c.shortcut || !matchShortcut(e, c.shortcut)) continue;
      if (!hasProject && !NO_PROJECT.has(c.id)) continue;
      // малый редактор (лист задания/реферата): перечисления, уровень и т. п. - в нём (runCommand направит туда)
      const inMini = !!(e.target as HTMLElement)?.closest?.('.mini-editor') && MINI_OK.has(c.id);
      if ((inField || inModal) && !GLOBAL.has(c.id) && !inMini) continue;
      e.preventDefault();
      runCommand(c.id);
      return;
    }
    // Ctrl+S и Ctrl+P браузера в редакторе не нужны никогда
    if (hasProject && (e.ctrlKey || e.metaKey) && !e.altKey && (e.code === 'KeyS' || e.code === 'KeyP')) e.preventDefault();
  });
}
